/**
 * Stop event handler for draggable node views.
 *
 * This handler allows drag, clipboard, keyboard, and composition/IME events to propagate
 * to ProseMirror while stopping other events that could interfere with the
 * node view's behavior. Keyboard events are needed so that Delete/Backspace can
 * remove selected nodes. Composition events are needed for emoji pickers and
 * input method editors within contenteditable areas inside node views.
 *
 * Use this with `stopEvent` option in `ReactNodeViewRenderer` or `AngularNodeViewRenderer`
 * for draggable atom nodes that use custom node views.
 */
export function draggableNodeStopEvent({ event }: { event: Event }): boolean {
  const isDragEvent = /dragstart|dragover|dragend|drop/.test(event.type);
  if (isDragEvent) {
    return false;
  }

  const isClipboardEvent = /copy|paste|cut/.test(event.type);
  if (isClipboardEvent) {
    return false;
  }

  const isKeyboardEvent = /keydown|keyup|keypress/.test(event.type);
  if (isKeyboardEvent) {
    return false;
  }

  const isCompositionEvent = /compositionstart|compositionend|compositionupdate|beforeinput/.test(event.type);
  if (isCompositionEvent) {
    return false;
  }

  return !/mousedown|drag|drop/.test(event.type);
}
